import { call, put, takeLatest, take, fork, cancel } from 'redux-saga/effects';
import {
  getHardwareProductsApi,
  getHardwareBaseProductsApi,
  createHardwareProductApi,
  updateHardwareProductApi,
  deleteHardwareProductApi,
  getHardwareCatalogueLogs,
} from 'api/hardwareCatalogue';
import { errorMessage, successMessage } from 'state/toast/actions';
import {
  GET_HARDWARE_PRODUCTS,
  GET_HARDWARE_BASE_PRODUCTS,
  SAVE_HARDWARE_PRODUCT,
  DELETE_HARDWARE_PRODUCT,
  HARDWARE_CATALOGUE_DO_SHOW_LOG,
  HARDWARE_CATALOGUE_LOG_CHANGE_PAGE,
  HARDWARE_CATALOGUE_LOG_SET_SHOWN,
} from './actionTypes';
import {
  updateHardwareProducts,
  setHardwareLoading,
  updateHardwareBaseProducts,
  setHardwareEditorLoading,
  setHardwareEditorError,
  showCreateHardwareModal,
  showEditHardwareModal,
  showDeleteHardwareConfirmation,
  setHardwareDeleteLoading,
  getHardwareProducts,
  setLogPage,
  setLogModalShown,
  setLogIsLoading,
  setLogData,
  setLogErrors,
  clearLogErrors,
  resetLogData,
} from './actions';

export function* handleGetHardwareProducts() {
  try {
    yield put(setHardwareLoading(true));
    const response = yield call(getHardwareProductsApi);
    yield put(updateHardwareProducts(response.data.data || []));
  } catch (error) {
    yield put(errorMessage('Error', 'Failed to load hardware products'));
  } finally {
    yield put(setHardwareLoading(false));
  }
}

export function* handleGetHardwareBaseProducts() {
  try {
    const response = yield call(getHardwareBaseProductsApi);
    yield put(updateHardwareBaseProducts(response.data.data || []));
  } catch (error) {
    yield put(errorMessage('Error', 'Failed to load hardware types'));
  }
}

export function* handleSaveHardwareProduct(action) {
  const { productPayload, isEditMode } = action.payload;
  try {
    yield put(setHardwareEditorLoading(true));
    yield put(setHardwareEditorError(null));
    if (isEditMode) {
      yield call(updateHardwareProductApi, productPayload);
    } else {
      yield call(createHardwareProductApi, productPayload);
    }
    yield put(showCreateHardwareModal(false));
    yield put(showEditHardwareModal(false, null));
    yield put(
      successMessage(
        'Success',
        `Hardware ${isEditMode ? 'updated' : 'registered'} successfully`
      )
    );
    yield put(getHardwareProducts());
  } catch (error) {
    const errorMsg =
      (error.response && error.response.data && error.response.data.message) ||
      `Failed to ${isEditMode ? 'update' : 'register'} hardware`;
    yield put(setHardwareEditorError(errorMsg));
  } finally {
    yield put(setHardwareEditorLoading(false));
  }
}

export function* handleDeleteHardwareProduct(action) {
  const { productId } = action.payload;
  try {
    yield put(setHardwareDeleteLoading(true));
    yield call(deleteHardwareProductApi, productId);
    yield put(showDeleteHardwareConfirmation(false, null));
    yield put(successMessage('Success', 'Hardware deleted successfully'));
    yield put(getHardwareProducts());
  } catch (error) {
    yield put(errorMessage('Error', 'Failed to delete hardware'));
  } finally {
    yield put(setHardwareDeleteLoading(false));
  }
}

function* logModalTask() {
  while (true) {
    const { page } = (yield take(HARDWARE_CATALOGUE_LOG_CHANGE_PAGE)).payload;
    yield put(setLogIsLoading(true));
    yield put(clearLogErrors());
    yield put(resetLogData());
    try {
      const {
        data: {
          data: { internal_tools_activities: activities },
          paging,
        },
      } = yield call(getHardwareCatalogueLogs, page, 20);
      yield put(setLogData(activities, paging));
    } catch (e) {
      yield put(setLogErrors(e.message || ['Failed to load logs']));
    } finally {
      yield put(setLogIsLoading(false));
    }
  }
}

export function* doShowLogs() {
  const task = yield fork(logModalTask);
  yield put(setLogPage(1));
  yield put(setLogModalShown(true));
  let closeModalRequest;
  do {
    closeModalRequest = (yield take(HARDWARE_CATALOGUE_LOG_SET_SHOWN)).payload;
  } while (closeModalRequest.shown);
  yield cancel(task);
}

export default function* hardwareCatalogueSaga() {
  yield takeLatest(GET_HARDWARE_PRODUCTS, handleGetHardwareProducts);
  yield takeLatest(GET_HARDWARE_BASE_PRODUCTS, handleGetHardwareBaseProducts);
  yield takeLatest(SAVE_HARDWARE_PRODUCT, handleSaveHardwareProduct);
  yield takeLatest(DELETE_HARDWARE_PRODUCT, handleDeleteHardwareProduct);
  yield takeLatest(HARDWARE_CATALOGUE_DO_SHOW_LOG, doShowLogs);
}
